// assets/habi-bundle.js

(function () {
  'use strict';

  const instances = new Map();

  class HabiBundle {
    constructor(el) {
      this.el            = el;
      this.sectionId     = el.dataset.sectionId;
      this.blockId       = el.dataset.blockId;
      this.tier1Disc     = parseFloat(el.dataset.tier1Discount || '0');
      this.tier2Disc     = parseFloat(el.dataset.tier2Discount || '0');
      this.tier2Enabled  = el.dataset.tier2Enabled === 'true';
      this.mainVariantId = parseInt(el.dataset.variantId || '0', 10);
      this.mainPrice     = parseInt(el.dataset.mainPrice || '0', 10);
      this.submitBtnId   = el.dataset.submitBtnId;
      this.formId        = el.dataset.formId;
      this.selected      = [];   // [{variantId, price, title}]
      this.variants      = this._loadVariants();
      this._init();
    }

    _loadVariants() {
      const el = document.getElementById(`HabiBundleVariants-${this.sectionId}`);
      try { return JSON.parse(el?.textContent || '[]'); } catch { return []; }
    }

    _init() {
      this._hideMainBtn();
      this._initBuyBtn();
      this._initPicker();
      this._initUnbundle();
      this._updateState();
      this._initVariantSync();
    }

    // ── Hide the standard ATC button ──────────────────────────────────

    _hideMainBtn() {
      const btn = document.getElementById(this.submitBtnId);
      if (btn) btn.style.display = 'none';
    }

    // ── Product picker — unlimited selections, toggle on/off ───────────
    // No cap on how many products can be selected. Discount tier is
    // determined purely by count: 1 → tier1%, 2+ → tier2%.

    _initPicker() {
      this.el.querySelectorAll('.habi-bundle__product-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-selected')) {
            btn.classList.remove('is-selected');
            btn.setAttribute('aria-pressed', 'false');
            this.selected = this.selected.filter(s => s.variantId !== btn.dataset.variantId);
          } else {
            btn.classList.add('is-selected');
            btn.setAttribute('aria-pressed', 'true');
            this.selected.push({
              variantId: btn.dataset.variantId,
              price:     parseInt(btn.dataset.price || '0', 10),
              title:     btn.dataset.title || '',
            });
          }
          this._updateState();
        });
      });
    }

    // ── Discount tier: 0 selected → 0%, 1 → tier1, 2+ → tier2 ────────

    _calcDiscount() {
      const n = this.selected.length;
      if (n === 0) return 0;
      if (n === 1) return this.tier1Disc;
      return this.tier2Enabled ? this.tier2Disc : this.tier1Disc;
    }

    // ── Single source of truth for all UI state ────────────────────────

    _updateState() {
      const bundleSum = this.selected.reduce((acc, s) => acc + s.price, 0);
      const combined  = this.mainPrice + bundleSum;
      const discount  = this._calcDiscount();
      const total     = this.selected.length === 0
        ? this.mainPrice
        : Math.round(combined * (1 - discount / 100));
      const savings   = combined - total;

      const buyBtn  = document.getElementById(`HabiBundleBuyBtn-${this.blockId}`);
      const summary = document.getElementById(`HabiBundleSummary-${this.blockId}`);
      const undoBtn = document.getElementById(`HabiBundleUndo-${this.blockId}`);

      // ── Buy button: label + price
      if (buyBtn) {
        const labelEl     = buyBtn.querySelector('.habi-bundle__buy-label');
        const priceEl     = buyBtn.querySelector('.habi-bundle__buy-price');
        const addLabel    = buyBtn.dataset.addLabel    || 'Add to cart';
        const bundleLabel = buyBtn.dataset.bundleLabel || 'Add bundle to cart';
        if (labelEl) labelEl.textContent = this.selected.length === 0 ? addLabel : bundleLabel;
        if (priceEl) priceEl.textContent = this._formatMoney(total);
      }

      // ── Summary: selected items, savings, total
      if (summary) {
        if (this.selected.length === 0) {
          summary.hidden = true;
        } else {
          summary.hidden = false;
          const withLabel = summary.dataset.withLabel || 'Bundle with:';
          const itemsEl   = summary.querySelector('.habi-bundle__selected-items');
          if (itemsEl) {
            itemsEl.innerHTML = this.selected
              .map(s =>
                `<div class="habi-bundle__summary-line">` +
                  `<span class="habi-bundle__summary-label">${withLabel}</span>` +
                  `<span class="habi-bundle__summary-value">${this._esc(s.title)}</span>` +
                `</div>`
              )
              .join('');
          }
          const savingsEl = summary.querySelector('.habi-bundle__savings-amount');
          if (savingsEl) savingsEl.textContent = this._formatMoney(savings);
          const totalEl = summary.querySelector('.habi-bundle__total-amount');
          if (totalEl) totalEl.textContent = this._formatMoney(total);
        }
      }

      // ── Unbundle button: visible only when at least one product picked
      if (undoBtn) undoBtn.hidden = this.selected.length === 0;

      // ── Progress bar + savings label percentage
      this._updateProgress();
      this._updateSavingsLabel(discount);
    }

    // ── Progress bar — how many more to reach next discount tier ──────────

    _updateProgress() {
      const progressEl = this.el.querySelector('.habi-bundle__progress');
      if (!progressEl) return;

      const textEl  = progressEl.querySelector('.habi-bundle__progress-text');
      const trackEl = progressEl.querySelector('.habi-bundle__progress-track');

      const n   = this.selected.length;
      const max = this.tier2Enabled ? 2 : 1;
      const isTierReached = n >= 1;

      progressEl.classList.toggle('is-tier-reached', isTierReached);

      // Colors from data attributes (set by Liquid) or hardcoded fallbacks
      const fillColor    = progressEl.dataset.fillColor    || '#e63946';
      const reachedColor = progressEl.dataset.reachedColor || '#2d6a1e';
      const activeColor  = isTierReached ? reachedColor : fillColor;

      // Paint the fill directly as a hard-stop gradient on the track's background.
      // This is the most direct approach: no child elements, no pseudo-elements,
      // no CSS variable inheritance — the track background IS the visual progress bar.
      if (trackEl) {
        const fillPct = n >= max ? 100 : Math.round(n / max * 100);
        trackEl.style.background = fillPct > 0
          ? `linear-gradient(to right, ${activeColor} 0% ${fillPct}%, #e5e7eb ${fillPct}% 100%)`
          : '';
        trackEl.setAttribute('aria-valuenow', fillPct);
      }

      // Mark milestones reached
      progressEl.querySelectorAll('.habi-bundle__progress-milestone').forEach(m => {
        m.classList.toggle('is-reached', n >= parseInt(m.dataset.milestone || '99', 10));
      });

      if (n >= max) {
        if (textEl) {
          textEl.className   = 'habi-bundle__progress-text habi-bundle__progress-text--max';
          textEl.textContent = progressEl.dataset.maxText || 'Maximum discount applied!';
        }
        return;
      }

      if (textEl) {
        textEl.className = 'habi-bundle__progress-text';
        const prompt   = progressEl.dataset.prompt || 'Add {n} more for {pct}% off';
        const nextDisc = n === 0 ? this.tier1Disc : this.tier2Disc;
        const needed   = (n === 0 ? 1 : 2) - n;
        textEl.innerHTML = prompt
          .replace('{n}',   needed)
          .replace('{pct}', `<strong class="habi-bundle__progress-pct">${nextDisc}</strong>`);
      }
    }

    // ── Savings label: append active discount percentage ─────────────────

    _updateSavingsLabel(discount) {
      const labelEl = document.getElementById(`HabiBundleSavingsLabel-${this.blockId}`);
      if (!labelEl) return;
      const base = labelEl.dataset.baseLabel || 'You save:';
      labelEl.textContent = discount > 0 ? `${base} (${discount}%)` : base;
    }

    // ── Unbundle — clear all selections ───────────────────────────────

    _initUnbundle() {
      const btn = document.getElementById(`HabiBundleUndo-${this.blockId}`);
      btn?.addEventListener('click', () => {
        this.selected = [];
        this.el.querySelectorAll('.habi-bundle__product-btn').forEach(b => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        this._updateState();
      });
    }

    // ── Resolve current variant ID from multiple DOM sources ───────────
    // Priority: product form hidden input → stored instance state → URL param.
    // This avoids relying on a single source that might be stale.

    _getCurrentVariantId() {
      // 1. Product form hidden input — most reliable, Dawn updates this live
      const form    = document.getElementById(this.formId);
      const idInput = form?.querySelector('input[name="id"]');
      if (idInput?.value) {
        const id = parseInt(idInput.value, 10);
        if (id) return id;
      }

      // 2. Instance state — set at init and updated by _initVariantSync
      if (this.mainVariantId) return this.mainVariantId;

      // 3. URL ?variant= param — Dawn keeps this in sync with selection
      const urlId = parseInt(new URLSearchParams(window.location.search).get('variant') || '0', 10);
      if (urlId) return urlId;

      return 0;
    }

    // ── Bundle buy button → POST /cart/add.js with all items ──────────

    _initBuyBtn() {
      const buyBtn = document.getElementById(`HabiBundleBuyBtn-${this.blockId}`);
      if (!buyBtn) return;

      buyBtn.addEventListener('click', async () => {
        const mainVariantId = this._getCurrentVariantId();
        if (!mainVariantId) {
          console.warn('[HabiBundle] no variant ID found — buy_buttons block may be missing');
          const origHTML = buyBtn.innerHTML;
          buyBtn.innerHTML = '<span>Variante nicht gefunden</span>';
          setTimeout(() => { buyBtn.innerHTML = origHTML; }, 2500);
          return;
        }

        buyBtn.disabled = true;
        buyBtn.setAttribute('aria-busy', 'true');
        const originalHTML = buyBtn.innerHTML;

        try {
          // Always include the main product; append any selected bundle products.
          // Read the personalisation textarea if it is visible and has content, then
          // pass its value as a Shopify line item property so it flows through cart →
          // checkout → order confirmation email → Shopify admin Orders automatically.
          const mainItem = { id: mainVariantId, quantity: 1 };
          const persoWrap = document.getElementById(`HabiVPWishWrap-${this.sectionId}`);
          if (persoWrap && !persoWrap.hidden) {
            const ta = persoWrap.querySelector('textarea');
            if (ta && ta.value.trim()) {
              const match = ta.name.match(/^properties\[(.+)\]$/);
              const key = match ? match[1] : 'Persönlicher Wunsch';
              mainItem.properties = { [key]: ta.value.trim() };
            }
          }
          const items = [mainItem];
          for (const s of this.selected) {
            items.push({ id: parseInt(s.variantId, 10), quantity: 1 });
          }

          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: {
              'Content-Type':     'application/json',
              'Accept':           'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });

          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.description || errData.message || `HTTP ${res.status}`);
          }

          // Items are in the cart — refresh the header count bubble, then open the drawer.
          // Each step is independently caught so a UI failure never masks a successful add.
          await this._refreshCartBubble();

          try {
            if (window.MFBCart?.open) {
              await window.MFBCart.refresh?.();
              window.MFBCart.open();
            } else {
              // Dawn's built-in cart-drawer or cart-notification custom element
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer?.open) {
                cartDrawer.open(buyBtn);
              } else {
                document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
                document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true }));
              }
            }
          } catch (uiErr) {
            console.warn('[HabiBundle] cart UI update failed', uiErr);
            document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true }));
          }
        } catch (err) {
          console.error('[HabiBundle] cart add failed', err);
          buyBtn.innerHTML = `<span>${this._esc(err.message) || 'Fehler – bitte erneut versuchen'}</span>`;
          setTimeout(() => { buyBtn.innerHTML = originalHTML; }, 2500);
        } finally {
          buyBtn.disabled = false;
          buyBtn.removeAttribute('aria-busy');
        }
      });
    }

    // ── Recalculate when selected variant changes ──────────────────────
    // MutationObserver fires after Dawn's viewTransition inserts a fresh
    // <variant-selects> element with [data-selected-variant] JSON. This
    // is the only reliable source when buy_buttons is disabled (no form).

    _initVariantSync() {
      const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!productInfo) return;

      const getVariantId = () => {
        const scriptEl = productInfo.querySelector('variant-selects [data-selected-variant]');
        if (scriptEl) {
          try {
            const v = JSON.parse(scriptEl.textContent);
            if (v?.id) return v.id;
          } catch (_) {}
        }
        // Fallback: form input (present when buy_buttons block is enabled)
        const form    = document.getElementById(this.formId);
        const idInput = form?.querySelector('input[name="id"]');
        if (idInput?.value) {
          const id = parseInt(idInput.value, 10);
          if (id) return id;
        }
        return 0;
      };

      const sync = () => {
        const variantId = getVariantId();
        if (!variantId) return;
        const variant = this.variants.find(v => v.id === variantId);
        if (variant) {
          this.mainVariantId = variantId;
          this.mainPrice     = variant.price;
          this._updateState();
        }
      };

      // Dawn inserts a brand-new VARIANT-SELECTS node on every option change
      // via HTMLUpdateUtility.viewTransition — watch for that insertion.
      new MutationObserver(mutations => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeName === 'VARIANT-SELECTS') { sync(); return; }
          }
        }
      }).observe(productInfo, { childList: true, subtree: true });
    }

    // ── Refresh header cart count bubble after a successful add ──────────
    // Primary: Section Rendering API (handles count + icon SVG swap atomically).
    // Fallback: fetch /cart.js and patch the DOM directly.

    async _refreshCartBubble() {
      try {
        const data = await fetch('/?sections=cart-icon-bubble', { credentials: 'same-origin' }).then(r => r.json());
        const html = data['cart-icon-bubble'];
        if (html) {
          const parser = new DOMParser();
          const doc    = parser.parseFromString(html, 'text/html');
          const newEl  = doc.getElementById('shopify-section-cart-icon-bubble');
          const oldEl  = document.getElementById('shopify-section-cart-icon-bubble');
          if (newEl && oldEl) { oldEl.innerHTML = newEl.innerHTML; return; }
        }
      } catch (_) {}

      // Fallback: update the count bubble directly without a section re-render
      try {
        const cart  = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        const count = cart.item_count;
        const link  = document.getElementById('cart-icon-bubble');
        if (!link) return;

        let bubble = link.querySelector('.cart-count-bubble');
        if (count > 0) {
          if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            link.appendChild(bubble);
          }
          const display = count < 100 ? count : '99+';
          bubble.innerHTML =
            `<span aria-hidden="true">${display}</span>` +
            `<span class="visually-hidden">${display}</span>`;
        } else {
          bubble?.remove();
        }
      } catch (e) {
        console.warn('[HabiBundle] cart bubble refresh failed', e);
      }
    }

    _formatMoney(cents) {
      // Follow the store's ACTIVE currency (set by Shopify Markets / the region chooser),
      // not a hardcoded EUR — so bundle totals switch to $, £, etc. like the rest of the theme.
      var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    }

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    destroy() {
      const btn = document.getElementById(this.submitBtnId);
      if (btn) btn.style.display = '';
      this.selected = [];
    }
  }

  // ── Init / destroy ─────────────────────────────────────────────────────

  function init(scope = document) {
    scope.querySelectorAll('[data-component="habi-bundle"]').forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new HabiBundle(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll('[data-component="habi-bundle"]').forEach(el => {
      if (instances.has(el)) {
        instances.get(el).destroy();
        instances.delete(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded',        () => init());
  document.addEventListener('shopify:section:load',    e  => init(e.target));
  document.addEventListener('shopify:section:unload',  e  => destroy(e.target));

})();
